'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { serviceMinQuantity, type ServiceMeasure } from '@/core/utils/product-action';
import type { Breakpoint } from './breakpoints';

/** Product-page blocks that carry their own control — the configurator leaves out its own copy
 *  of each one the page already has (one price, one quantity field, one "Dodaj do koszyka"). */
export type ProductPageControl = 'price' | 'quantity' | 'add-to-cart';

interface ProductContextType {
  selectedAttributes: Record<string, string>;
  setSelectedAttributes: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  selectedVariant: any;
  product: any;
  handleAttributeSelect: (attrName: string, val: string) => void;
  configuratorPrice: number | null;
  setConfiguratorPrice: (price: number | null) => void;
  configuratorSelections: Record<number, number[]>;
  setConfiguratorSelections: React.Dispatch<React.SetStateAction<Record<number, number[]>>>;
  /** Returns false when nothing was added (required options missing) — "Kup teraz" must not
   *  then go on to the checkout. */
  configurableAddToCart: (() => boolean) | null;
  setConfigurableAddToCart: React.Dispatch<React.SetStateAction<(() => boolean) | null>>;
  /** A per-m² service's piece dimensions, entered on the page (ServiceMeasureFields). */
  serviceMeasure: ServiceMeasure | null;
  setServiceMeasure: React.Dispatch<React.SetStateAction<ServiceMeasure | null>>;
  /** Per control, how many page blocks show it on each breakpoint. */
  pageControls: Partial<Record<ProductPageControl, Partial<Record<Breakpoint, number>>>>;
  /** Called by a block on mount with the breakpoints it's shown on; returns the unmount cleanup. */
  registerPageControl: (control: ProductPageControl, breakpoints: readonly Breakpoint[]) => () => void;
  /** Shared across product-quantity, product-price and product-add-to-cart blocks, so
   * a merchant can place them anywhere on the page and have quantity stay in sync. */
  quantity: number;
  setQuantity: React.Dispatch<React.SetStateAction<number>>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

/** Every BlockRenderer with a product wraps its blocks in a provider, nested ones (rows, columns,
 *  containers) included — so on a template with the gallery in one column and the variant picker
 *  in another, each column had its own selected variant and the gallery never followed the pick
 *  (dlaszwalni 2026-10-22). A nested provider for the same product reuses the outer state. */
export function ProductProvider({ product, children }: { product: any; children: React.ReactNode }) {
  const outer = useContext(ProductContext);
  if (outer && outer.product?.id === product?.id) return <>{children}</>;
  return <ProductStateProvider product={product}>{children}</ProductStateProvider>;
}

function ProductStateProvider({ product, children }: { product: any; children: React.ReactNode }) {
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [configuratorPrice, setConfiguratorPrice] = useState<number | null>(null);
  const [configuratorSelections, setConfiguratorSelections] = useState<Record<number, number[]>>({});
  const [configurableAddToCart, setConfigurableAddToCart] = useState<(() => boolean) | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [serviceMeasure, setServiceMeasure] = useState<ServiceMeasure | null>(null);
  const [pageControls, setPageControls] = useState<Partial<Record<ProductPageControl, Partial<Record<Breakpoint, number>>>>>({});
  const registerPageControl = useCallback((control: ProductPageControl, breakpoints: readonly Breakpoint[]) => {
    const shift = (delta: number) => setPageControls((prev) => {
      const counts = { ...(prev[control] || {}) };
      for (const bp of breakpoints) counts[bp] = Math.max(0, (counts[bp] || 0) + delta);
      return { ...prev, [control]: counts };
    });
    shift(1);
    return () => shift(-1);
  }, []);

  // A variant/product switch invalidates whatever quantity was picked for the previous
  // selection (e.g. it may exceed the new variant's stock) — reset to 1 rather than
  // silently carrying over a number the merchant never chose for this item.
  // A service starts at its minimum load ("min. 4 kg"), never below it.
  useEffect(() => {
    setQuantity(serviceMinQuantity(product));
    setServiceMeasure(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    if (product && product.product_type === 'variable') {
      const defVariant = product.variants?.find((v: any) => v.is_default) || product.variants?.[0];
      if (defVariant) {
        setSelectedAttributes(defVariant.attributes || {});
      }
    }
  }, [product]);

  const selectedVariant = product?.product_type === 'variable'
    ? product.variants?.find((v: any) => {
        return Object.entries(selectedAttributes).every(([key, val]) => v.attributes[key] === val) &&
               Object.keys(v.attributes).length === Object.keys(selectedAttributes).length;
      }) || product.variants?.find((v: any) => v.is_default) || product.variants?.[0] || null
    : null;

  const handleAttributeSelect = (attrName: string, val: string) => {
    if (!product || !product.variants) return;
    const newSelection = { ...selectedAttributes, [attrName]: val };
    
    // 1. Check if a perfect variant matches this combination
    const perfectVariantExists = product.variants.some((v: any) => {
        return Object.entries(newSelection).every(([k, sVal]) => v.attributes[k] === sVal) &&
               Object.keys(v.attributes).length === Object.keys(newSelection).length;
    });
    
    if (perfectVariantExists) {
        setSelectedAttributes(newSelection);
        return;
    }
    
    // 2. Find any variant containing [attrName]: val
    const possibleVariants = product.variants.filter((v: any) => v.attributes[attrName] === val);
    if (possibleVariants.length > 0) {
        // Find one that shares the most selected attributes
        let bestVariant = possibleVariants[0];
        let maxMatches = -1;
        for (const v of possibleVariants) {
            let matches = 0;
            for (const [k, sVal] of Object.entries(newSelection)) {
                if (v.attributes[k] === sVal) {
                    matches++;
                }
            }
            if (matches > maxMatches) {
                maxMatches = matches;
                bestVariant = v;
            }
        }
        setSelectedAttributes(bestVariant.attributes || {});
    } else {
        setSelectedAttributes(newSelection);
    }
  };

  return (
    <ProductContext.Provider value={{
      selectedAttributes,
      setSelectedAttributes,
      selectedVariant,
      product,
      handleAttributeSelect,
      configuratorPrice,
      setConfiguratorPrice,
      configuratorSelections,
      setConfiguratorSelections,
      configurableAddToCart,
      setConfigurableAddToCart,
      quantity,
      setQuantity,
      serviceMeasure,
      setServiceMeasure,
      pageControls,
      registerPageControl,
    }}>
      {children}
    </ProductContext.Provider>
  );
}

export function useProductState() {
  const context = useContext(ProductContext);
  return context;
}
